import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    const initAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        if (mounted) {
          console.log('? Auth initialized, user:', response.data.user);
          setUser(response.data.user);
        }
      } catch (error) {
        if (mounted) {
          console.log('?? Not authenticated');
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      mounted = false;
    };
  }, []); // Empty deps - only run ONCE

  const login = async (email, password) => {
    console.log('?? Login function called');
    const response = await api.post('/auth/login', { email, password });
    console.log('?? Login response received:', response.data);
    setUser(response.data.user);
    return response.data.user;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    window.location.href = '/login';
  };

  const isAdmin = () => {
    return ['ADMIN', 'SUPER_ADMIN'].includes(user?.role?.roleCode);
  };

  const isSuperAdmin = () => {
    return user?.role?.roleCode === 'SUPER_ADMIN';
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    const perms = user.role?.permissions || {};
    return perms.all === true || perms[permission] === true;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAdmin,
    isSuperAdmin,
    hasPermission,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};